import React, { useEffect, useState } from 'react';
import { Box, Pagination, Stack, Typography, Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Chip, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper, Alert } from '@mui/material';
import { fetchAllNewsletters, updateNewsletter } from '../../controllers/newsletterController';
import { resolveNewsletterAssetUrl } from '../../api/httpClientNewsletter';
import CreateNewsletterDialog from './CreateNewsletterDialog';
import EditNewsletterDialog from '../../components/EditNewsletterDialog';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { deleteNewsletter as deleteNewsletterAction } from '../../controllers/newsletterController';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const NewsletterList = () => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createSuccessMessage, setCreateSuccessMessage] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedNewsletterId, setSelectedNewsletterId] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data, meta } = await fetchAllNewsletters({ pageNumber: page, pageSize });
        if (!active) return;
        setItems(data || []);
        setTotalPages(meta?.totalPages || 1);
      } catch (e) {
        if (!active) return;
        setError(e?.message || 'Bültenler alınamadı');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [page, pageSize]);

  const handleActiveToggle = async (newsletter) => {
    try {
      await updateNewsletter({ 
        id: newsletter.id, 
        title: newsletter.title, 
        order: newsletter.order || 0, 
        isActive: !newsletter.isActive, 
        isPublished: newsletter.isPublished || false,
        publishedAtUtc: newsletter.publishedAtUtc || null,
      });
      const { data, meta } = await fetchAllNewsletters({ pageNumber: page, pageSize });
      setItems(data || []);
      setTotalPages(meta?.totalPages || 1);
    } catch (e) {
      console.error('Aktif durumu güncellenemedi:', e);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const datePart = date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
    const timePart = date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    return `${datePart} - ${timePart}`;
  };

  const handleEditOpen = (newsletter) => {
    setSelectedNewsletterId(newsletter.id);
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setSelectedNewsletterId(null);
    // Refresh list
    (async () => {
      try {
        const { data, meta } = await fetchAllNewsletters({ pageNumber: page, pageSize });
        setItems(data || []);
        setTotalPages(meta?.totalPages || 1);
      } catch (e) {}
    })();
  };

  const handleEditSuccess = () => {
    // Refresh list
    (async () => {
      try {
        const { data, meta } = await fetchAllNewsletters({ pageNumber: page, pageSize });
        setItems(data || []);
        setTotalPages(meta?.totalPages || 1);
      } catch (e) {}
    })();
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Bültenler</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ width: 16 }} />}
          sx={{
            textTransform: 'none',
            backgroundColor: '#E7EDF4',
            boxShadow: 'none',
            fontSize: 13,
            borderRadius: '5px',
            color: '#0E4D94',
            '&:hover': {
              backgroundColor: '#CFDBEA',
              boxShadow: 'none',
            },
          }}
          onClick={() => setCreateOpen(true)}
        >
          Yeni Bülten
        </Button>
      </Stack>
      {createSuccessMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCreateSuccessMessage('')}>
          {createSuccessMessage}
        </Alert>
      )}
      {error ? (
        <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>
      ) : (
        <>
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
              border: '1px solid #f5f5f5',
              py: 1,
              px: 2.5
            }}
          >
            <Table size="small">
              <TableHead sx={{ bgcolor: 'transparent' }}>
                <TableRow>
                  <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Görsel</TableCell>
                  <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Başlık</TableCell>
                  <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Oluşturulma Tarihi</TableCell>
                  <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Yayınlanma Tarihi</TableCell>
                  <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Durum</TableCell>
                  <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: pageSize }).map((_, index) => (
                    <TableRow
                      key={`skeleton-${index}`}
                      sx={{
                        '& .MuiTableCell-root': {
                          borderBottom: index === pageSize - 1 ? 'none' : '1px dashed #e8e8e8',
                          px: 1.5,
                          py: 1.5,
                        },
                      }}
                    >
                      <TableCell>
                        <Skeleton width={50} height={50} borderRadius={4} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={250} height={20} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={150} height={20} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={150} height={20} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={60} height={24} borderRadius={16} />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Skeleton width={32} height={32} borderRadius={6} />
                          <Skeleton width={32} height={32} borderRadius={6} />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ color: '#a3a3a3' }}>Henüz bülten eklenmemiş</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((newsletter, index) => (
                    <TableRow
                      key={newsletter.id}
                      hover
                      sx={{
                        '& .MuiTableCell-root': {
                          borderBottom: index === items.length - 1 ? 'none' : '1px dashed #e8e8e8',
                          px: 1.5,
                          py: 1.5,
                          color: '#444444',
                          fontWeight: 500,
                        },
                      }}
                    >
                      <TableCell>
                        {newsletter.coverImageUrl ? (
                    <Box
                      component="img"
                      src={resolveNewsletterAssetUrl(newsletter.coverImageUrl)}
                      alt={newsletter.title}
                            sx={{
                              width: 50,
                              height: 50,
                              objectFit: 'cover',
                              borderRadius: '4px',
                              cursor: 'pointer',
                            }}
                            onClick={() => handleEditOpen(newsletter)}
                          />
                        ) : (
                          <Box
                      sx={{ 
                              width: 50,
                              height: 50,
                              borderRadius: '4px',
                              backgroundColor: '#f5f5f5',
                        display: 'flex', 
                        alignItems: 'center', 
                              justifyContent: 'center',
                            }}
                          >
                            <ImageIcon sx={{ fontSize: 24, color: '#a3a3a3' }} />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ cursor: 'pointer' }} onClick={() => handleEditOpen(newsletter)}>{newsletter.title}</TableCell>
                      <TableCell>{formatDate(newsletter.createdAtUtc)}</TableCell>
                      <TableCell>{formatDate(newsletter.publishedAtUtc)}</TableCell>
                      <TableCell>
                        {newsletter.isActive ? (
                          <Chip
                            label="Aktif"
                            size="small"
                            sx={{
                              backgroundColor: '#DFFFEA',
                              color: '#17C653',
                              fontWeight: 600,
                              fontSize: '12px'
                            }}
                          />
                        ) : (
                          <Chip
                            label="Pasif"
                            size="small"
                        sx={{ 
                              backgroundColor: '#FFEEF3',
                              color: '#F8285A',
                              fontWeight: 600,
                              fontSize: '12px'
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <Tooltip title="Düzenle">
                  <IconButton 
                    size="small" 
                    onClick={(e) => { 
                      e.stopPropagation(); 
                                handleEditOpen(newsletter);
                              }}
                              sx={{
                                backgroundColor: '#E7EDF4',
                                borderRadius: '6px',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: '#CFDBEA',
                                },
                                '& svg': {
                                  fontSize: '16px',
                                  color: '#0E4D94',
                                },
                    }}
                  >
                              <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Sil">
                  <IconButton 
                    size="small" 
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      setSelected(newsletter); 
                      setConfirmOpen(true); 
                    }}
                              sx={{
                                backgroundColor: '#FFEEF3',
                                borderRadius: '6px',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: '#FFD6E0',
                                },
                                '& svg': {
                                  fontSize: '16px',
                                  color: '#F8285A',
                                },
                              }}
                  >
                              <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} />
      </Box>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Bülteni Sil
          <IconButton
            onClick={() => setConfirmOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <DialogContentText sx={{ fontSize: '14px', color: '#666666' }}>
            {selected ? `"${selected.title}" bültenini silmek istediğinize emin misiniz?` : 'Seçili bülteni silmek istediğinize emin misiniz?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
            if (!selected) return;
            try {
              await deleteNewsletterAction(selected.id);
              setConfirmOpen(false);
              setSelected(null);
              const { data, meta } = await fetchAllNewsletters({ pageNumber: page, pageSize });
              setItems(data || []);
              setTotalPages(meta?.totalPages || 1);
            } catch (e) {}
            }}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#F8285A',
              borderRadius: '5px',
              px: 2,
              '&:hover': {
                backgroundColor: '#d91e47',
              },
            }}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>
      <CreateNewsletterDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async ({ sendPushNotification } = {}) => {
          setCreateOpen(false);
          if (sendPushNotification) {
            setCreateSuccessMessage('Bülten oluşturuldu ve üyelere bildirim gönderildi.');
          } else {
            setCreateSuccessMessage('');
          }
          const { data, meta } = await fetchAllNewsletters({ pageNumber: page, pageSize });
          setItems(data || []);
          setTotalPages(meta?.totalPages || 1);
        }}
      />
      <EditNewsletterDialog
        open={editOpen}
        newsletterId={selectedNewsletterId}
        onClose={handleEditClose}
        onSuccess={handleEditSuccess}
      />
    </Box>
  );
};

export default NewsletterList;
